/**
 * Public read-only view of a Doserne daily plan.
 *
 * Route: `/p/plan/:slug` — no login required. The backend returns the plan
 * only if the admin has flipped `is_public` on. Otherwise → 404.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Calendar, MapPin, Users, Zap, ExternalLink } from "lucide-react";
import RichContent from "../doserne/RichContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isHtml(str) {
  return typeof str === "string" && /<\/?[a-z][\s\S]*>/i.test(str);
}

export default function PublicDailyPlan() {
  const { slug } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/plan/${slug}`);
        setPlan(data);
        document.title = `${data.title} · Doserne Academy`;
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "error");
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafaf7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400"/>
      </div>
    );
  }

  if (error === "not_found" || !plan) {
    return (
      <div className="min-h-screen bg-[#fafaf7] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center mb-4">
          <Zap className="w-8 h-8 text-cyan-400" strokeWidth={3}/>
        </div>
        <h1 className="text-2xl font-black text-slate-900 mb-2">Planul nu este disponibil</h1>
        <p className="text-slate-500 max-w-sm">
          Link-ul a expirat, a fost dezactivat de administrator sau este incorect.
        </p>
        <Link to="/doserne" className="mt-6 inline-flex items-center gap-1.5 text-cyan-700 hover:text-cyan-900 font-semibold text-sm">
          Deschide Doserne Academy <ExternalLink className="w-4 h-4"/>
        </Link>
      </div>
    );
  }

  const bg = plan.background_color || "#ffffff";
  const bodyIsHtml = isHtml(plan.body);

  return (
    <div className="min-h-screen bg-[#fafaf7]">
      {/* Brand bar — Doserne Academy only, no AllStillas cross-reference. */}
      <header className="bg-[#050505] text-white px-4 sm:px-6 py-3 flex items-center gap-3">
        <Link to="/doserne" className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center hover:scale-105 transition-transform">
          <Zap className="w-4 h-4 text-slate-900" strokeWidth={3}/>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-black">Doserne Academy</div>
          <div className="text-sm font-black leading-tight">Delt plan · Publikk visning</div>
        </div>
        <Link to="/doserne" className="text-xs text-cyan-400 hover:text-cyan-200 font-semibold inline-flex items-center gap-1" data-testid="public-brand-link">
          Doserne Academy →
        </Link>
      </header>

      {/* Plan card */}
      <main className="max-w-3xl mx-auto p-4 sm:p-6">
        <article
          className="border-2 border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm"
          style={{ backgroundColor: bg }}
          data-testid="public-plan-card"
        >
          <div className="flex items-center gap-2 mb-2 text-xs text-slate-500 font-mono">
            <Calendar className="w-3.5 h-3.5"/>
            {plan.plan_date}
            {plan.priority && plan.priority !== "normal" && (
              <span className={`ml-1 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full ${plan.priority === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                {plan.priority}
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mb-4" data-testid="public-plan-title">
            {plan.title}
          </h1>

          {plan.body && (
            bodyIsHtml
              ? <RichContent html={plan.body} className="text-sm sm:text-base text-slate-700"/>
              : <p className="text-sm sm:text-base text-slate-700 whitespace-pre-wrap leading-relaxed">{plan.body}</p>
          )}

          {plan.teams?.length > 0 && (
            <div className="mt-5 space-y-2">
              {plan.teams.map((t, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-white/70 border border-slate-200 rounded-lg">
                  <Users className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    {t.location && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3"/> {t.location}</div>}
                    {t.workers?.length > 0 && <div className="text-xs text-slate-600 mt-1">{t.workers.join(" · ")}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-slate-200 text-[10px] text-slate-400">
            {plan.created_by_name ? <>Publisert av {plan.created_by_name}</> : "Publisert av Doserne Academy"}
          </div>
        </article>

        <p className="text-center text-xs text-slate-400 mt-4">
          Denne siden er offentlig — kun visning. For full tilgang, logg inn på{" "}
          <a href="/doserne/logg-inn" className="text-cyan-700 hover:text-cyan-900 font-semibold">Doserne Academy</a>.
        </p>
      </main>
    </div>
  );
}
